import { Button } from '../Button';
import { ButtonSize, ButtonType } from '@common/types/button';
import { Icon } from '../Icon';
import { IconType } from '@src/IconType';
import { classNames } from '@src/utils/classNames';
import { type HTMLAttributes, useMemo } from 'react';
import { Body4 } from '../typography/Body4';

export type ToastProps = Readonly<{
  message: string;
  type?: 'info' | 'success' | 'error';
  actionLabel?: string;
  actionCallback?: () => void;
  closable?: boolean;
  onClose?: () => void;
}> &
  HTMLAttributes<HTMLDivElement>;

export function Toast({
  message,
  type = 'info',
  actionLabel,
  actionCallback,
  closable = false,
  onClose,
  ...rest
}: ToastProps) {
  const typeClass = useMemo(() => {
    switch (type) {
      case 'info':
        return 'bg-blue-50 border-blue-500';
      case 'success':
        return 'bg-green-50';
      case 'error':
        return 'bg-orange-50 border-orange-600';
      default:
        return 'bg-gray-50';
    }
  }, [type]);

  const iconType = useMemo(() => {
    switch (type) {
      case 'info':
        return IconType.Info;
      case 'success':
        return IconType.Check;
      case 'error':
        return IconType.Xcircle;
      default:
        return IconType.Lamp;
    }
  }, [type]);

  const iconColor = useMemo(() => {
    switch (type) {
      case 'info':
        return 'text-blue-500';
      case 'success':
        return 'text-green-500';
      case 'error':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  }, [type]);

  return (
    <div
      className={classNames(
        'p-3 z-[var(--z-index-toast)]',
        'rounded border-1 border-solid',
        'flex text-black items-center max-w-md',
        typeClass,
      )}
      role={type === 'error' ? 'alert' : 'status'}
      {...rest}
    >
      <Icon
        type={iconType}
        className={classNames('size-6 mr-2 shrink-0 self-start', iconColor)}
      ></Icon>
      <Body4 className="grow">{message}</Body4>
      {actionLabel && actionCallback && (
        <Button
          className="ml-2"
          onClick={actionCallback}
          buttonType={ButtonType.PRIMARY}
          label={actionLabel}
          size={ButtonSize.SMALL}
        />
      )}
      {closable && onClose && (
        <Button
          testId="close-button"
          type="button"
          IconLeading={IconType.X}
          buttonType={ButtonType.ICON_ONLY}
          onClick={onClose}
          className="!p-0 shrink-0 self-start"
          iconClassName="size-6"
        />
      )}
    </div>
  );
}
